import { useEffect, useMemo, useState } from 'react';
import { Combobox, Option, OptionGroup } from '@fluentui/react-components';

/** Persona, empresa o profesional que puede figurar como receptor de un pago. */
export interface ReceiverOption {
    id: string;
    name: string;
    kind: 'practitioner' | 'person';
}

const MAX_OPTIONS = 50;

/** Línea secundaria de cada opción (la tarjeta no es redonda: es una fila rectangular de la lista). */
export const receiverKindLabel = (kind: ReceiverOption['kind']): string => (kind === 'practitioner' ? 'Profesional' : 'Persona o empresa');

const OptionContent = ({ option, suffix }: { option: ReceiverOption; suffix?: string }) => (
    <span className="cff-payee-option">
        <span>{suffix ? `${option.name} ${suffix}` : option.name}</span>
        {/* Sólo visual: el nombre accesible de la opción sigue siendo el nombre. */}
        <span className="cff-payee-option__detail" aria-hidden>{receiverKindLabel(option.kind)}</span>
    </span>
);

const normalize = (text: string): string =>
    (text || '').toLowerCase().normalize('NFD').replace(new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g'), '');

/**
 * Elige a nombre de quién se registra el pago. El primero de la lista es el
 * profesional dueño de la deuda (el valor por defecto); después, otros
 * profesionales y las personas o empresas. Se busca escribiendo.
 */
export const ReceiverPicker = ({ options, value, defaultId, onChange, ariaLabel }: {
    options: ReceiverOption[];
    value: string | undefined;
    /** El dueño de la deuda: va primero y se marca como "por defecto". */
    defaultId: string;
    onChange: (id: string) => void;
    ariaLabel?: string;
}) => {
    const nameOf = (id: string | undefined) => options.find((option) => option.id === id)?.name ?? '';
    const [text, setText] = useState<string>(nameOf(value));
    const [open, setOpen] = useState<boolean>(false);

    // Si cambia el elegido o llegan los nombres, el texto muestra el nombre resuelto.
    useEffect(() => {
        setText(nameOf(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, options.length]);

    const query = normalize(text);
    const selectedName = nameOf(value);
    const filtering = query.length > 0 && text !== selectedName;

    const visible = useMemo(() => {
        const matches = filtering ? options.filter((option) => normalize(option.name).includes(query)) : options;
        return matches.slice(0, MAX_OPTIONS);
    }, [options, query, filtering]);

    const owner = visible.filter((option) => option.id === defaultId);
    const practitioners = visible.filter((option) => option.id !== defaultId && option.kind === 'practitioner');
    const persons = visible.filter((option) => option.id !== defaultId && option.kind === 'person');

    return (
        <Combobox
            aria-label={ariaLabel ?? 'Receptor del pago'}
            value={text}
            selectedOptions={value ? [value] : []}
            open={open}
            onOpenChange={(_event, data) => setOpen(data.open)}
            onChange={(event) => { setText((event.target as HTMLInputElement).value); setOpen(true); }}
            onOptionSelect={(_event, data) => {
                if (!data.optionValue || data.optionValue === 'no-matches') return;
                onChange(data.optionValue);
                setText(nameOf(data.optionValue));
                setOpen(false);
            }}
            onBlur={() => setText(selectedName)}
            data-testid="payment-receiver-picker"
            className="cff-payee-picker"
            appearance="outline"
        >
            {owner.map((option) => (
                <Option key={option.id} value={option.id} text={option.name}><OptionContent option={option} suffix="(dueño de la deuda)" /></Option>
            ))}
            {practitioners.length > 0 && (
                <OptionGroup label="Otros profesionales">
                    {practitioners.map((option) => <Option key={option.id} value={option.id} text={option.name}><OptionContent option={option} /></Option>)}
                </OptionGroup>
            )}
            {persons.length > 0 && (
                <OptionGroup label="Personas y empresas">
                    {persons.map((option) => <Option key={option.id} value={option.id} text={option.name}><OptionContent option={option} /></Option>)}
                </OptionGroup>
            )}
            {visible.length === 0 && <Option key="no-matches" value="no-matches" text="" disabled>Nadie coincide con la búsqueda</Option>}
        </Combobox>
    );
};

/**
 * Opciones de receptor: el dueño de la deuda primero, después los demás
 * profesionales y las personas (sin repetir identificadores ni borradas).
 */
export const buildReceiverOptions = (input: {
    ownerId: string;
    ownerName: string;
    practitioners: { userId: string; fullName?: string; name?: string }[];
    persons: { personIdentifier: string; businessName?: string; isDeleted?: boolean }[];
    excludeIds?: string[];
}): ReceiverOption[] => {
    const seen = new Set<string>((input.excludeIds ?? []).map((id) => id.toLowerCase()));
    const out: ReceiverOption[] = [];
    const push = (id: string | undefined, name: string | undefined, kind: ReceiverOption['kind']) => {
        if (!id || seen.has(id.toLowerCase())) return;
        seen.add(id.toLowerCase());
        out.push({ id, name: (name || '').trim() || id, kind });
    };
    push(input.ownerId, input.ownerName, 'practitioner');
    [...input.practitioners]
        .sort((a, b) => (a.fullName || a.name || '').localeCompare(b.fullName || b.name || ''))
        .forEach((item) => push(item.userId, item.fullName || item.name, 'practitioner'));
    [...input.persons]
        .filter((item) => !item.isDeleted)
        .sort((a, b) => (a.businessName || '').localeCompare(b.businessName || ''))
        .forEach((item) => push(item.personIdentifier, item.businessName, 'person'));
    return out;
};
